import React, { useState, useEffect } from 'react';
import { Plane, MapPin, ExternalLink, TrendingDown, Info, Hotel } from 'lucide-react';
import { BarChart, Bar, Tooltip, ResponsiveContainer, Cell } from 'recharts';

const TravelFXCard = ({ cityId = 'cuzco' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const theme = {
    bg: '#09090b', card: '#18181b', border: '#27272a',
    text: '#e4e4e7', muted: '#a1a1aa', accent: '#3b82f6',
    success: '#10b981', warning: '#f59e0b',
  };

  useEffect(() => {
    setLoading(true);
    fetch(`/api/travel/api/v1/planning/${cityId}`)
      .then(res => res.json())
      .then(json => {
        setData(json);
        setLoading(false);
      })
      .catch(err => {
        console.error(`FX API Error (${cityId}):`, err);
        setLoading(false);
      });
  }, [cityId]);

  // --- THE NORMALIZATION LAYER (FIXES PARIS/NYC) ---

  // 1. FLIGHT PRICE 
  const getFlightPrice = () => {
    // Check Manual Format (Cusco) -> Raw Best Flights (Paris) -> Raw Price Insight (NYC)
    return data?.flight_stats?.best_price || 
           data?.best_flights?.[0]?.price || 
           data?.flight_stats?.lowest_price || 0;
  };

  // 2. THE RESERVE BUTTON LINK 
  const getBookingLink = () => {
    // NYC/Paris use search_metadata.google_flights_url
    // Cusco used flight_stats.url
    return data?.search_metadata?.google_flights_url || 
           data?.flight_stats?.url || null;
  };

  // 3. TYPICAL RANGE 
  const getTypicalRange = () => {
    const range = data?.flight_stats?.price_insight || data?.flight_stats?.typical_price_range;
    return Array.isArray(range) ? range : [0, 0];
  };

  // 4. HOTEL PRICE STRING 
  const formatHotelPrice = (hotel) => {
    const val = hotel.rate_per_night;
    if (!val) return "N/A";
    if (typeof val === 'string') return val;
    return val.lowest || val.extracted_lowest || "N/A";
  };

  // 5. HOTEL PRICE NUMBER 
  const parseHotelNum = (hotel) => {
    const val = hotel.rate_per_night;
    if (!val) return 0;
    const str = typeof val === 'object' ? (val.lowest || "") : String(val);
    return parseInt(str.replace(/\D/g, '')) || 0;
  };

  if (loading) return <div style={{color: theme.muted, padding: 20}}>Syncing {cityId.toUpperCase()}...</div>;
  if (!data) return null;

  const flightLink = getBookingLink();
  const flightPrice = getFlightPrice();
  const typicalRange = getTypicalRange();
  const hotels = data.hotel_stats || [];

  return (
    <div style={{
      background: theme.card, border: `1px solid ${theme.border}`,
      borderRadius: '12px', padding: '24px', color: theme.text,
      fontFamily: 'sans-serif', width: '100%'
    }}>
      {/* HEADER */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '24px' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: theme.accent, fontSize: '0.7rem', fontWeight: 'bold', textTransform: 'uppercase' }}>
            <MapPin size={14} /> TRAVEL PLAN FX
          </div>
          <h2 style={{ margin: '4px 0', fontSize: '1.5rem' }}>{data.metadata?.target_city || cityId}</h2>
        </div>
        <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '1.2rem', fontWeight: 'bold' }}>BRL</div>
            <div style={{ fontSize: '0.6rem', color: theme.success }}>LIVE FEED</div>
        </div>
      </div>

      {/* FLIGHT SECTION */}
      <div style={{ background: 'rgba(255,255,255,0.02)', borderRadius: '8px', padding: '16px', marginBottom: '20px', borderLeft: `4px solid ${theme.accent}` }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '0.9rem', color: theme.muted }}>
            <Plane size={18} /> Round Trip
          </div>
          {/* FIXED LINK LOGIC */}
          {flightLink && (
            <a href={flightLink} target="_blank" rel="noreferrer" style={{ 
              color: '#fff', background: theme.accent, padding: '4px 8px', borderRadius: '4px',
              fontSize: '0.7rem', fontWeight: 'bold', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 
            }}>
              RESERVE <ExternalLink size={12}/>
            </a>
          )}
        </div>
        <div style={{ fontSize: '1.8rem', fontWeight: 'bold' }}>
          {flightPrice > 0 ? `R$ ${flightPrice.toLocaleString()}` : 'Check Live'}
        </div>
        {typicalRange[0] > 0 && (
          <div style={{ marginTop: '8px', fontSize: '0.7rem', color: theme.muted }}>
            Range: R$ {typicalRange[0]} - R$ {typicalRange[1]}
          </div>
        )}
      </div>

      {/* CHART */}
      <div style={{ height: '100px', marginBottom: '16px' }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={hotels}>
            <Tooltip contentStyle={{ background: theme.card, border: `1px solid ${theme.border}`, fontSize: '0.7rem' }} />
            <Bar dataKey={(d) => parseHotelNum(d)} radius={[2, 2, 0, 0]}>
              {hotels.map((_, index) => (
                <Cell key={index} fill={index === 0 ? theme.accent : '#27272a'} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* HOTEL LIST */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {hotels.slice(0, 3).map((hotel, i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 12px', background: 'rgba(255,255,255,0.01)', borderRadius: '6px', fontSize: '0.85rem' }}>
            <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '180px' }}>{hotel.name}</div>
            <div style={{ color: theme.success, fontWeight: 'bold' }}>{formatHotelPrice(hotel)}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TravelFXCard;